import { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { AnimatePresence } from "motion/react";
import { CommentCard } from "../components/CommentCard";
import { BulkValidationBar } from "../components/BulkValidationBar";
import { UserHistoryDrawer } from "../components/UserHistoryDrawer";
import { ServerConfig, fullHttpURL } from "../serverConfig";
import { commentKey, fullName } from "../core";
import { palette } from "../theme";
import type { ActionType, Comment } from "../generated/core-types";

interface CommentsToValidateProps {
  serverConfig: ServerConfig;
  comments: Comment[];
  onValidate: (comment: Comment, action: ActionType) => void;
}

async function fetchUserArchive(url: string, userName: string): Promise<Comment[]> {
  const response = await fetch(`${url}/live/comments/archives/${encodeURIComponent(userName)}`);
  if (!response.ok) return [];
  const text = await response.text();
  if (!text) return [];
  const all = JSON.parse(text) as Comment[];
  // Deduplicate by timestamp like the previous Kotlin code did.
  const byTimestamp = new Map<string, Comment>();
  for (const c of all) {
    if (!byTimestamp.has(c.timestamp)) byTimestamp.set(c.timestamp, c);
  }
  return Array.from(byTimestamp.values()).sort((a, b) => a.timestamp.localeCompare(b.timestamp));
}

export function CommentsToValidate({
  serverConfig,
  comments,
  onValidate,
}: CommentsToValidateProps): JSX.Element {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerUser, setDrawerUser] = useState<string>("");
  const [drawerComments, setDrawerComments] = useState<Comment[]>([]);

  const total = comments.length;
  const showEmpty = total === 0;

  const headline = useMemo(() => {
    if (total === 0) return "Không có bình luận cần xem lại";
    if (total === 1) return "1 bình luận cần xem lại";
    return `${total} bình luận cần xem lại`;
  }, [total]);

  const showHistoryFor = async (c: Comment) => {
    const name = fullName(c.user);
    setDrawerUser(name);
    setDrawerOpen(true);
    setDrawerComments([]);
    const archive = await fetchUserArchive(fullHttpURL(serverConfig), name);
    setDrawerComments(archive);
  };

  const onBulkValidate = (count: number) => {
    comments.slice(0, count).forEach((c) => onValidate(c, c.action));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
        <Typography variant="h2" sx={{ m: 0 }}>
          {headline}
        </Typography>
        {total > 0 && (
          <Typography variant="body2" sx={{ color: palette.inkSoft }}>
            phân loại hoặc xác nhận từng bình luận
          </Typography>
        )}
      </Box>

      {showEmpty ? (
        <EmptyState />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <AnimatePresence mode="popLayout" initial={false}>
            {comments.map((comment) => (
              <CommentCard
                key={commentKey(comment)}
                comment={comment}
                onValidate={(action) => onValidate(comment, action)}
                onShowHistory={() => void showHistoryFor(comment)}
              />
            ))}
          </AnimatePresence>
        </Box>
      )}

      <BulkValidationBar total={total} onBulkValidate={onBulkValidate} />

      <UserHistoryDrawer
        open={drawerOpen}
        userName={drawerUser}
        comments={drawerComments}
        onClose={() => setDrawerOpen(false)}
      />
    </Box>
  );
}

function EmptyState(): JSX.Element {
  return (
    <Box
      sx={{
        py: { xs: 6, md: 9 },
        textAlign: "center",
        border: `1px dashed ${palette.border}`,
        borderRadius: 12,
        backgroundColor: palette.surface,
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 120 28"
        sx={{ width: 120, height: 28, mb: 2, color: palette.border }}
        aria-hidden
      >
        <path
          d="M 4 14 Q 12 4, 20 14 T 36 14 T 52 14 T 68 14 T 84 14 T 100 14 T 116 14"
          stroke="currentColor"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
        />
      </Box>
      <Typography variant="h3" sx={{ m: 0, mb: 1, color: palette.ink }}>
        Đã xử lý xong
      </Typography>
      <Typography variant="body2" sx={{ color: palette.inkSoft }}>
        Bình luận mới sẽ xuất hiện tại đây.
      </Typography>
    </Box>
  );
}
